/** IdleMon moves runtime — catalog + learnsets + loadout helpers. */

const EQUIP_SLOTS = 4;
const MAX_LEVEL = 300;
const MAX_UNLOCK_LEVEL = 150;
const MOVE_DATA_VERSION = '20260728-myst-fx-map-1';

/** Catálogo de golpes AoE ensinados por TM (slot liberado no nível 100). */
export const TM_AOE_MOVES = Object.freeze(Object.fromEntries([
  ['aoe-electric', 'Campo de Descarga', 'elétrico', 'special', 82],
  ['aoe-fire', 'Onda de Inferno', 'fogo', 'special', 86],
  ['aoe-water', 'Onda de Maré', 'água', 'special', 84],
  ['aoe-grass', 'Tempestade de Pétalas', 'planta', 'special', 82],
  ['aoe-bug', 'Ataque de Enxame', 'inseto', 'physical', 80],
  ['aoe-flying', 'Furacão', 'voador', 'special', 84],
  ['aoe-normal', 'Hiper Voz', 'normal', 'special', 78],
  ['aoe-psychic', 'Onda Psíquica', 'psíquico', 'special', 84],
  ['aoe-rock', 'Deslizamento de Rochas', 'pedra', 'physical', 86],
  ['aoe-ground', 'Terremoto', 'terra', 'physical', 88],
  ['aoe-ghost', 'Vento Sinistro', 'fantasma', 'special', 82],
  ['aoe-dragon', 'Meteoro Draco', 'dragão', 'special', 90],
  ['aoe-fighting', 'Onda de Vácuo', 'lutador', 'special', 82],
  ['aoe-poison', 'Onda de Lodo', 'veneno', 'special', 84],
  ['aoe-dark', 'Rosnado Sombrio', 'sombrio', 'special', 82],
  ['aoe-steel', 'Tempestade de Aço', 'aço', 'physical', 86],
  ['aoe-ice', 'Nevasca', 'gelo', 'special', 86],
  ['aoe-fairy', 'Brilho Mágico', 'fada', 'special', 84],
].map(([id, name, type, category, power]) => [id, Object.freeze({
  id,
  name,
  type,
  category,
  power,
  accuracy: 100,
  cooldown: 5.25,
  range: 10,
  targeting: 'area',
  aoe: true,
  tier: 'tm',
  tm: true,
  minLevel: 100,
})])));

let movesCatalog = null;
let learnsetsData = null;
let moveFxData = null;
let loadPromise = null;

export function getEquipSlots() {
  return EQUIP_SLOTS;
}
export function getMaxLevel() {
  return MAX_LEVEL;
}
export function getMaxUnlockLevel() {
  return MAX_UNLOCK_LEVEL;
}

/**
 * Converte o cooldown técnico do catálogo em turnos fáceis de entender.
 * Um turno de recarga significa que o golpe pula a próxima ação do jogador.
 * Golpes básicos single-target ficam sempre disponíveis; golpes de alto
 * impacto, área e status chegam a 4 turnos.
 */
export function moveCooldownTurns(move) {
  const power = Math.max(0, Number(move?.power) || 0);
  if (move?.tier === 'basic' && move?.targeting !== 'area'
    && move?.category !== 'status' && power <= 60) return 0;
  const raw = Math.max(1.2, Number(move?.cooldown) || 2.5);
  return Math.max(1, Math.min(4, Math.round(raw / 1.75)));
}

/** Distância de engajamento no mapa. Usa o alcance real importado quando existe. */
export function moveCombatRange(move) {
  const authored = Number(move?.range);
  // Piso 6: sprites grandes parecem colados antes dos centros entrarem no range antigo (4).
  if (Number.isFinite(authored) && authored > 0) return Math.max(6, Math.min(14, authored));
  if (move?.targeting === 'area') return 11;
  return move?.category === 'special' ? 10 : 6;
}

export async function loadMovesData() {
  if (movesCatalog && learnsetsData) return { moves: movesCatalog, learnsets: learnsetsData, fx: moveFxData };
  if (loadPromise) return loadPromise;
  loadPromise = Promise.all([
    fetch(`public/game/moves.json?v=${MOVE_DATA_VERSION}`).then((r) => r.json()),
    fetch(`public/game/learnsets.json?v=${MOVE_DATA_VERSION}`).then((r) => r.json()),
    // Versionado igual aos outros: sem isso o mapa de efeitos ficava preso no
    // cache do navegador e toda skill continuava usando a animação antiga.
    fetch(`public/game/move-fx.json?v=${MOVE_DATA_VERSION}`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
  ]).then(([movesJson, learnJson, fxJson]) => {
    movesCatalog = { ...(movesJson.moves || {}), ...TM_AOE_MOVES };
    learnsetsData = learnJson.learnsets || {};
    moveFxData = fxJson?.byMove || {};
    return { moves: movesCatalog, learnsets: learnsetsData, fx: moveFxData };
  });
  return loadPromise;
}

export function isMovesReady() {
  return !!(movesCatalog && learnsetsData);
}

export function getMoveFx(id) {
  return moveFxData?.[id] || null;
}

export function getMove(id) {
  return movesCatalog?.[id] || null;
}

/** Moves that can participate in the automatic hunt loop. Status-only skills
 * are intentionally kept in the catalog for future non-hunt interactions, but
 * they must never consume an idle combat slot. */
export function isCombatMove(moveOrId) {
  const move = typeof moveOrId === 'string' ? getMove(moveOrId) : moveOrId;
  return !!move && move.category !== 'status' && Number(move.power) > 0 && move.targeting !== 'self';
}

export function learnsetFor(species) {
  if (!species || !learnsetsData) return [];
  const key = String(species).toLowerCase().replace(/_/g, '-');
  return learnsetsData[key]?.moves || learnsetsData[species]?.moves || [];
}

export function unlockedMoves(species, level) {
  const lvl = Math.min(MAX_UNLOCK_LEVEL, Math.max(1, level || 1));
  return learnsetFor(species)
    .filter((entry) => entry.level <= lvl)
    .map((entry) => entry.move);
}

export function newlyUnlockedMoves(species, oldLevel, newLevel) {
  return learnsetFor(species)
    .filter((entry) => entry.level > oldLevel && entry.level <= newLevel)
    .map((entry) => entry.move);
}

/** Prefere dano forte e AoE; status sem implementação nunca ocupa o ciclo idle. */
export function defaultLoadout(species, level) {
  const unlocked = unlockedMoves(species, level);
  if (!unlocked.length) return [];
  const scored = unlocked.map((id) => {
    const mv = getMove(id);
    if (!mv) return { id, score: -1 };
    if (!isCombatMove(mv)) return { id, score: -Infinity };
    let score = mv.power || 0;
    if (mv.targeting === 'area') score += 35;
    if (mv.tier === 'basic') score += 5;
    return { id, score };
  });
  scored.sort((a, b) => b.score - a.score);
  const picks = [];
  // Reserva um slot para o melhor AoE aprendido; produtividade de área não
  // pode desaparecer porque quatro golpes single-target pontuaram um pouco mais.
  const bestArea = scored.find((entry) => entry.score >= 0 && getMove(entry.id)?.targeting === 'area');
  if (bestArea) picks.push(bestArea.id);
  for (const s of scored) {
    if (picks.length >= EQUIP_SLOTS) break;
    if (s.score < 0 || picks.includes(s.id)) continue;
    picks.push(s.id);
  }
  // Completa apenas com golpes funcionais; lista vazia usa o ataque STAB fallback.
  while (picks.length < EQUIP_SLOTS && picks.length < unlocked.length) {
    const next = scored.find((entry) => entry.score >= 0 && !picks.includes(entry.id))?.id;
    if (!next) break;
    picks.push(next);
  }
  return picks.slice(0, EQUIP_SLOTS);
}

export function sanitizeLoadout(species, level, equipped) {
  const unlocked = new Set(unlockedMoves(species, level));
  const clean = (equipped || []).filter((id) => unlocked.has(id) && isCombatMove(id)).slice(0, EQUIP_SLOTS);
  if (clean.length) return clean;
  return defaultLoadout(species, level);
}

/**
 * Escolhe o melhor golpe pronto. Equipar mais opções nunca força o uso de um
 * golpe pior: o contexto de combate define a pontuação e a ordem dos slots só
 * desempata. cooldowns: Record<moveId, readyAtPlayerTurn>.
 */
export function pickNextMove(equipped, cooldowns, playerTurn, scoreMove = () => 0) {
  const list = (equipped || []).filter(Boolean);
  if (!list.length) return { moveId: null, score: -Infinity };
  const ready = list.map((id, index) => {
    const available = (cooldowns?.[id] || 0) <= playerTurn;
    const rawScore = available ? Number(scoreMove(id)) : -Infinity;
    return { id, index, score: Number.isFinite(rawScore) ? rawScore : -Infinity };
  }).filter((entry) => entry.score > -Infinity)
    .sort((a, b) => b.score - a.score || a.index - b.index);
  return ready.length ? { moveId: ready[0].id, score: ready[0].score } : { moveId: null, score: -Infinity };
}

export function moveDisplayName(id) {
  const mv = getMove(id);
  return mv?.name || id;
}
